/**
 * KeralaHome — starter Kerala-branded landing.
 *
 * Preview modes:
 *   /kerala                → always shows this page
 *   /?site=kerala          → swaps the homepage for this page
 *
 * All data comes from the parent Social Pulse API; nothing here is
 * Kerala-only backend logic. This is what the standalone Kerala
 * sibling site will look like when it ships as its own Emergent
 * app — this repo just previews the child brand.
 *
 * Palette + copy tokens live in this file so tweaks don't touch the
 * global design system.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, Waves, Sparkles, MapPin, Bookmark, Newspaper } from "lucide-react";
import PlaceStamp from "@/components/PlaceStamp";
import PlaceKindTag from "@/components/PlaceKindTag";
import { UnsplashCredit } from "@/components/UnsplashPhoto";
import LocationWeatherPanel from "@/components/LocationWeatherPanel";

const API = process.env.REACT_APP_BACKEND_URL;

// Palette drawn from a Kerala houseboat + coconut-grove reference.
// Kept local so tweaks here never affect the parent's design tokens.
const K = {
  bg:        "#FBF7EA",          // pale coconut cream
  ink:       "#0D3B2E",          // rainforest ink
  inkSoft:   "#3C6B5D",
  accent:    "#0E7C55",          // banana-leaf green
  accentSoft:"#B7E4C7",
  gold:      "#C08A2E",          // brass lamp
  goldSoft:  "#F5E6C4",
  wine:      "#8B2E4E",          // pookalam red
  wineSoft:  "#F7D7DE",
};

const HERO_LINES = [
  "God's Own Country — every district, town and heritage story on one page.",
  "Live news, community pulses and open directories from all 14 districts.",
];

export default function KeralaHome() {
  const [places, setPlaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pulses, setPulses] = useState([]);
  const [heroPhoto, setHeroPhoto] = useState(null);

  useEffect(() => {
    let alive = true;
    axios.get(`${API}/api/locations-children/india/kerala`, { params: { limit: 100 } })
      .then((r) => alive && setPlaces(r.data?.items || []))
      .catch(() => alive && setPlaces([]))
      .finally(() => alive && setLoading(false));
    // Latest Kerala-tagged pulses via the multi-site scoping we just wired up.
    axios.get(`${API}/api/pulse-items`, { params: { site_source: "kerala", limit: 6 } })
      .then((r) => alive && setPulses(r.data?.items || []))
      .catch(() => { /* non-fatal */ });
    // Unsplash hero backdrop — coconut grove / backwaters. Fails soft
    // if the Unsplash key is unavailable; the gradient still holds.
    axios.get(`${API}/api/unsplash/search`, {
      params: { q: "kerala coconut grove backwaters", per_page: 1, orientation: "landscape" },
    })
      .then((r) => alive && setHeroPhoto(r.data?.results?.[0] || null))
      .catch(() => { /* non-fatal */ });
    return () => { alive = false; };
  }, []);

  const districts = useMemo(
    () => places.filter((p) => (p.kind || "").toLowerCase() === "district")
                .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [places]
  );
  const cities = useMemo(
    () => places.filter((p) => ["city", "town", "village"].includes((p.kind || "").toLowerCase()))
                .sort((a, b) => (a.name || "").localeCompare(b.name || "")),
    [places]
  );

  return (
    <div style={{ background: K.bg, color: K.ink }} data-testid="kerala-home">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{
          background: `linear-gradient(135deg, ${K.accent} 0%, ${K.ink} 100%)`,
          color: "#fff",
        }}
      >
        {heroPhoto?.image_url && (
          <div className="absolute inset-0 opacity-40 mix-blend-luminosity" aria-hidden="true">
            <img
              src={heroPhoto.image_url}
              alt=""
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        )}
        <div className="max-w-6xl mx-auto px-4 lg:px-6 py-16 sm:py-24 relative">
          <div className="grid md:grid-cols-[1fr_auto] items-center gap-8">
            <div>
              <span
                className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest rounded-full px-3 py-1"
                style={{ background: K.goldSoft, color: K.gold }}
                data-testid="kerala-brand-chip"
              >
                <Waves className="w-3 h-3" /> A Social Pulse sibling site · Preview
              </span>
              <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl leading-tight tracking-tight">
                Kerala Pulse
              </h1>
              <p className="mt-3 text-lg max-w-[46ch] opacity-90">{HERO_LINES[0]}</p>
              <p className="text-sm opacity-80 mt-1">{HERO_LINES[1]}</p>

              <div className="mt-6 flex flex-wrap gap-2">
                <Link
                  to="/l/india/kerala"
                  className="rounded-full px-4 py-2 text-sm font-semibold shadow-sm hover:shadow-md transition"
                  style={{ background: K.gold, color: "#fff" }}
                  data-testid="kerala-cta-portal"
                >
                  Open the Kerala portal →
                </Link>
                <Link
                  to="/directory/indian-districts"
                  className="rounded-full px-4 py-2 text-sm font-semibold border-2 bg-white/10 hover:bg-white/20 transition"
                  style={{ borderColor: "#fff" }}
                  data-testid="kerala-cta-districts"
                >
                  All 14 districts
                </Link>
              </div>
            </div>

            {/* Kerala state stamp */}
            <PlaceStamp
              kind="state"
              slug="india/kerala"
              size={160}
              className="shadow-lg ring-4 ring-white/40 hidden md:block"
              title="Kerala"
              testid="kerala-hero-stamp"
            />
          </div>
        </div>
      </section>

      {/* Unsplash attribution — only renders when the API served a photo */}
      {heroPhoto?.photographer && (
        <div
          className="max-w-6xl mx-auto px-4 lg:px-6 pt-2"
          data-testid="kerala-hero-credit-strip"
        >
          <UnsplashCredit
            photographer={heroPhoto.photographer}
            unsplashHref={heroPhoto.unsplash_link}
            align="right"
            className="text-xs"
            testid="kerala-hero-credit"
          />
        </div>
      )}

      {/* 14 Kerala districts — the whole point of this landing */}
      <section className="max-w-6xl mx-auto px-4 lg:px-6 py-12" data-testid="kerala-districts">
        <div className="flex items-baseline gap-2 mb-6">
          <MapPin className="w-4 h-4" style={{ color: K.accent }} />
          <h2 className="font-display text-3xl" style={{ color: K.ink }}>Every district</h2>
          <span className="text-xs font-mono opacity-60">
            {loading ? "…" : districts.length}
          </span>
        </div>
        {loading ? (
          <div className="opacity-60 text-sm"><Loader2 className="w-4 h-4 animate-spin inline mr-1" /> Loading districts…</div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {districts.map((d) => (
              <Link
                key={d.slug}
                to={`/l/${d.slug}`}
                className="group flex items-center gap-3 rounded-xl border-2 p-3 hover:shadow-md hover:-translate-y-0.5 transition"
                style={{ borderColor: K.accentSoft, background: "#fff" }}
                data-testid={`kerala-district-${d.slug}`}
              >
                <PlaceStamp kind="district" slug={d.slug} override={d.stamp_url} size={44} className="shadow-sm ring-1 ring-white/60" title={d.name} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate group-hover:underline" style={{ color: K.ink }}>{d.name}</div>
                  <div className="mt-0.5">
                    <PlaceKindTag kind="district" slug={d.slug} country="india" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>

      {/* Cities & towns */}
      {cities.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 lg:px-6 py-6 pb-12" data-testid="kerala-cities">
          <div className="flex items-baseline gap-2 mb-6">
            <Sparkles className="w-4 h-4" style={{ color: K.gold }} />
            <h2 className="font-display text-3xl" style={{ color: K.ink }}>Cities &amp; towns</h2>
            <span className="text-xs font-mono opacity-60">{cities.length}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {cities.slice(0, 24).map((c) => (
              <Link
                key={c.slug}
                to={`/l/${c.slug}`}
                className="group flex items-center gap-3 rounded-xl border p-3 hover:shadow-md transition"
                style={{ borderColor: K.goldSoft, background: "#fff" }}
                data-testid={`kerala-city-${c.slug}`}
              >
                <PlaceStamp kind="city" slug={c.slug} override={c.stamp_url} size={36} className="shadow-sm ring-1 ring-white/60" title={c.name} />
                <div className="min-w-0 flex-1">
                  <div className="font-medium truncate group-hover:underline" style={{ color: K.ink }}>{c.name}</div>
                  <div className="mt-0.5">
                    <PlaceKindTag kind="city" slug={c.slug} country="india" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
          {cities.length > 24 && (
            <div className="mt-4 text-center">
              <Link to="/directory/indian-cities" className="text-sm underline decoration-2 underline-offset-4" style={{ color: K.accent }}>
                See all Indian cities →
              </Link>
            </div>
          )}
        </section>
      )}

      {/* Latest Kerala pulses (only if the site_source stream has data) */}
      {pulses.length > 0 && (
        <section className="max-w-6xl mx-auto px-4 lg:px-6 py-6 pb-12" data-testid="kerala-pulses">
          <div className="flex items-baseline gap-2 mb-6">
            <Newspaper className="w-4 h-4" style={{ color: K.wine }} />
            <h2 className="font-display text-3xl" style={{ color: K.ink }}>Fresh pulses</h2>
            <span className="text-xs font-mono opacity-60">{pulses.length}</span>
          </div>
          <ul className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {pulses.map((p) => (
              <li key={p.id || p.slug} className="rounded-xl border p-3 bg-white" style={{ borderColor: K.wineSoft }}>
                <div className="text-xs font-mono uppercase tracking-widest opacity-60">{p.kind || "pulse"}</div>
                <div className="mt-1 font-medium leading-tight" style={{ color: K.ink }}>{p.title || p.name}</div>
                {p.body && <div className="mt-1 text-xs opacity-80 line-clamp-3">{p.body}</div>}
              </li>
            ))}
          </ul>
        </section>
      )}

      {/* Kerala-wide Weather Almanac + citizen reports — auto-hides when empty. */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-8">
        <LocationWeatherPanel slug="kerala" />
      </section>

      {/* Footer strip — preview note */}
      <footer className="py-8 px-4 text-center text-xs" style={{ background: K.ink, color: "#fff" }} data-testid="kerala-footer">
        <p className="opacity-80">
          <Bookmark className="w-3 h-3 inline mr-1" />
          <span className="font-mono uppercase tracking-widest">Preview mode</span> —
          this page renders inside the Social Pulse parent repo. In production it will
          ship as its own Emergent app at <strong>kerala.socialpulse.com</strong>.
        </p>
        <div className="mt-3">
          <Link to="/" className="underline opacity-80 hover:opacity-100">← Back to Social Pulse</Link>
        </div>
      </footer>
    </div>
  );
}

